/**
 * messageAuthorship — WHO COMPOSED THE WORDS in a thread message (client twin).
 *
 * THE FINDING THIS EXISTS FOR (Miles, live avatar test 2026-09-10): "he was
 * messaging on behalf of me and shouldn't under any circumstance." The thread
 * showed HIS authorship for words Prest0n wrote, because a message doc's only
 * author-ish field was `isUser?: boolean` — there was nowhere to record THAT
 * PREST0N WROTE IT.
 *
 * The vocabulary, orthogonal to the two fields that already exist:
 *   • `isUser`   — whose NAME AND AVATAR the message presents under (unchanged);
 *   • `senderId` — WHICH person, in a room with several (unchanged);
 *   • `authorship.composedBy` — WHO WROTE THE WORDS (new, and the only new fact).
 *
 * ABSENT ⇒ TODAY'S RENDERING, BYTE FOR BYTE: every message ever written carries
 * no authorship, every reader here returns null for it, and the render falls
 * back to the `isUser` split.
 *
 * VERBATIM TWIN of functions/src/prest0nSession/messageAuthorship.ts (separate
 * tsconfig roots — the groupThreadMessageDoc.ts idiom). Behavioural parity is
 * pinned by tests/prest0nSession/messageAuthorship.parity.test.ts.
 *
 * PURE: no React, no Firebase, no clock.
 */

export const MESSAGE_AUTHOR_PREST0N = 'prest0n' as const;
export const MESSAGE_AUTHOR_HUMAN = 'human' as const;
export const MESSAGE_AUTHORS = [MESSAGE_AUTHOR_PREST0N, MESSAGE_AUTHOR_HUMAN] as const;
export type MessageAuthor = (typeof MESSAGE_AUTHORS)[number];

/** Where a delegation was initiated. Absent ⇒ unknown surface; the marker still shows. */
export const DELEGATION_ORIGINS = ['voice', 'chat'] as const;
export type DelegationOrigin = (typeof DELEGATION_ORIGINS)[number];

/** The one field name. A nested map so the four facts are written and read as one. */
export const AUTHORSHIP_FIELD = 'authorship' as const;

export interface MessageAuthorship {
  /** Who composed the words. */
  composedBy: MessageAuthor;
  /**
   * Set ONLY on a delegated post: the uid whose name and avatar the message
   * presents under, while `composedBy` says Prest0n wrote it.
   */
  onBehalfOf?: string;
  /** Where the delegation was initiated ('voice' today). */
  via?: DelegationOrigin;
  /** When the delegation was declared (epoch ms). */
  declaredAt?: number;
}

/** Any message-shaped record: the server ThreadMessageDoc and the client ChatMessage both satisfy it. */
export interface AuthorshipCarrier {
  isUser?: boolean;
  authorship?: unknown;
  [key: string]: unknown;
}

/**
 * A uid we are willing to present a message under. Deliberately strict: this
 * string ends up naming a PERSON on screen, so no whitespace (a label-spoofing
 * vector) and no path separators (it is used as a map key beside doc paths).
 */
const UID_SHAPE = /^[^\s/]{1,128}$/;

function safeUid(raw: unknown): string {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return UID_SHAPE.test(s) ? s : '';
}

/** Prest0n's own authorship — every message he composes under his own name. */
export function prest0nAuthorship(): MessageAuthorship {
  return { composedBy: MESSAGE_AUTHOR_PREST0N };
}

/** A person's own authorship — they typed it. */
export function humanAuthorship(): MessageAuthorship {
  return { composedBy: MESSAGE_AUTHOR_HUMAN };
}

/**
 * A DECLARED delegation: Prest0n composed it, presented under `onBehalfOf`.
 * Returns null when the delegation is not well-formed — a caller that cannot
 * name whose behalf it is acting on has not declared anything.
 */
export function delegatedAuthorship(args: {
  onBehalfOf: unknown;
  via?: unknown;
  declaredAt?: unknown;
}): MessageAuthorship | null {
  const uid = safeUid(args.onBehalfOf);
  if (!uid) return null;
  const via = DELEGATION_ORIGINS.includes(String(args.via ?? '') as DelegationOrigin)
    ? (String(args.via) as DelegationOrigin)
    : undefined;
  const at = Number(args.declaredAt);
  const declaredAt = Number.isFinite(at) && at > 0 ? Math.floor(at) : undefined;
  return {
    composedBy: MESSAGE_AUTHOR_PREST0N,
    onBehalfOf: uid,
    ...(via ? { via } : {}),
    ...(declaredAt ? { declaredAt } : {}),
  };
}

/** Parse a stored/declared authorship value. Null for anything unrecognised. */
export function normalizeAuthorship(raw: unknown): MessageAuthorship | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;
  const composedBy = String(obj.composedBy ?? '').trim();
  if (composedBy === MESSAGE_AUTHOR_HUMAN) {
    // A human-authored stamp never carries a delegation: the two are exclusive.
    return { composedBy: MESSAGE_AUTHOR_HUMAN };
  }
  if (composedBy !== MESSAGE_AUTHOR_PREST0N) return null;
  const uid = safeUid(obj.onBehalfOf);
  if (!uid) return prest0nAuthorship();
  return delegatedAuthorship({ onBehalfOf: uid, via: obj.via, declaredAt: obj.declaredAt });
}

/** The authorship on a message, or null when it carries none (every legacy row). */
export function readAuthorship(message: AuthorshipCarrier | null | undefined): MessageAuthorship | null {
  if (!message) return null;
  return normalizeAuthorship((message as Record<string, unknown>)[AUTHORSHIP_FIELD]);
}

export interface MessageDelegation {
  onBehalfOf: string;
  via: DelegationOrigin | null;
  declaredAt: number | null;
}

/**
 * The delegation on a message, or null.
 *
 * STRICT BY DESIGN — all three must hold: the message presents as a person's
 * (`isUser === true`), the composer is Prest0n, and a behalf-uid is named. A
 * stale or half-written field can therefore never manufacture a delegation, and
 * because Firestore's merge writes never DELETE a key, a real one can never be
 * erased. That asymmetry is the permanence T-R8 asks for.
 */
export function delegationOf(message: AuthorshipCarrier | null | undefined): MessageDelegation | null {
  if (!message || message.isUser !== true) return null;
  const authorship = readAuthorship(message);
  if (!authorship || authorship.composedBy !== MESSAGE_AUTHOR_PREST0N) return null;
  const uid = safeUid(authorship.onBehalfOf);
  if (!uid) return null;
  return {
    onBehalfOf: uid,
    via: authorship.via ?? null,
    declaredAt: authorship.declaredAt ?? null,
  };
}

/** True when this message presents as a person's but Prest0n composed it. */
export function isDelegatedPost(message: AuthorshipCarrier | null | undefined): boolean {
  return delegationOf(message) !== null;
}

export type PlatformWriteVerdict =
  | { ok: true; authorship: MessageAuthorship }
  | { ok: false; reason: string };

/**
 * THE DOOR'S VERDICT (T1, 2026-09-10) — pure, so the negative control is a unit test.
 *
 * Every write through the platform's thread writer is composed by the platform:
 * a person's own typing reaches Firestore from their own client. So a message
 * presenting as a person's (`isUser: true`) is either a DECLARED delegation —
 * honoured, because following a clear instruction is the point (T-R10) — or an
 * undeclared impersonation, which is refused. An assistant write needs no
 * declaration: it is Prest0n's own, and is stamped as his (T-R7).
 */
export function resolvePlatformWriteAuthorship(
  message: AuthorshipCarrier | null | undefined,
): PlatformWriteVerdict {
  const declared = readAuthorship(message);
  if (!message || message.isUser !== true) {
    // Prest0n's own words. A doc that renders as HIS but claims a person wrote
    // them (or names a behalf) is a contradiction, and mis-attribution in that
    // direction is no better — refuse rather than pick a winner.
    if (declared && (declared.composedBy === MESSAGE_AUTHOR_HUMAN || declared.onBehalfOf)) {
      return {
        ok: false,
        reason:
          'thread message renders as Prest0n\'s own but declares a human author or a behalf-uid '
          + '— set isUser: true to post under a person\'s name, or drop the declaration',
      };
    }
    return { ok: true, authorship: prest0nAuthorship() };
  }
  if (!declared) {
    return {
      ok: false,
      reason:
        'human-authored thread message from a platform writer with no declared authorship '
        + '(pass authorship: delegatedAuthorship({ onBehalfOf, via }) to post on someone\'s behalf)',
    };
  }
  if (declared.composedBy === MESSAGE_AUTHOR_HUMAN) {
    return {
      ok: false,
      reason:
        'thread message declared composedBy \'human\' but written by a platform writer — '
        + 'a person\'s own words reach the thread from their own client, never through this door',
    };
  }
  const uid = safeUid(declared.onBehalfOf);
  if (!uid) {
    return {
      ok: false,
      reason: 'delegated thread message names no onBehalfOf uid — whose behalf is unstated',
    };
  }
  return { ok: true, authorship: declared };
}

/**
 * The write rule for a message that presents under a NAMED person (the group
 * store's human docs, whose `senderId` is that person).
 *
 * Same doctrine as the door above, plus one extra fact this shape makes
 * checkable: a delegation must name the SAME person the message presents as.
 * A mismatch is refused rather than reconciled — presenting Prest0n's words
 * under uid A while claiming behalf of uid B is mis-attribution whichever half
 * you believe. On READ the marker shows regardless (see delegationOf): an
 * inconsistent row is still not hand-typed, and hiding the mark is the one
 * outcome that deceives.
 */
export function resolvePresentedWriteAuthorship(
  presentedAs: string,
  declared: unknown,
): PlatformWriteVerdict {
  const d = normalizeAuthorship(declared);
  // No declaration, or an explicit human one ⇒ the person typed it. This is the
  // ordinary case: the group store MIRRORS a message that already arrived from
  // that person's own authenticated client.
  if (!d || d.composedBy === MESSAGE_AUTHOR_HUMAN) {
    return { ok: true, authorship: humanAuthorship() };
  }
  const behalf = safeUid(d.onBehalfOf);
  if (!behalf) {
    return {
      ok: false,
      reason: 'delegated message names no onBehalfOf uid — whose behalf is unstated',
    };
  }
  const presented = safeUid(presentedAs);
  if (!presented || behalf !== presented) {
    return {
      ok: false,
      reason: `delegation names ${behalf} but the message presents as ${presented || '(unnamed)'}`,
    };
  }
  return { ok: true, authorship: d };
}
